import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box } from '@mui/material'
import * as R from 'ramda'
import { Defaults, Text, useInterval } from '@pbt/pbt-ui-components'

import type { Estimate as GraphqlEstimate } from '~/api/graphql/generated/types'
import { PuiPagination } from '~/components/common/lists/table/PuiPagination'
import { EstimateBillingActivityTable } from '~/components/dashboard/clients/balance/table/estimates/EstimateBillingActivityTable'
import {
  fetchEstimatePage,
  getClientBillingActivityEstimateError,
  getEstimateLoading,
  getEstimatePageData,
  getEstimateTotalCount,
  getIsPollingEstimates,
  getShouldRefreshData,
} from '~/store/duck/clientBillingActivityData'
import { getFinanceIsSaving } from '~/store/reducers/finance'
import { usePersistClickedItemOnURL } from '~/utils/routes'

import { useWidgetPageManagementHooks } from '../../../hooks/widgetPageManagementHooks'
import { CLIENT_BILLING_ACTIVITY_VISIBLE_ROWS } from '../../table/common/tableConstants'

interface EstimatesWidgetProps {
  initialPage?: number
  isExpanded: boolean
  onClickEstimate: (data: GraphqlEstimate) => void
}

export const EstimatesWidget = ({
  isExpanded,
  onClickEstimate,
  initialPage = 1,
}: EstimatesWidgetProps) => {
  const dispatch = useDispatch()
  const { t } = useTranslation('Common')
  const { clientId } = useParams()
  const persistClickedItem = usePersistClickedItemOnURL('estimateWidgetPage')

  const { offsetValue, setURLState } = useWidgetPageManagementHooks({
    initialPage,
    itemsPerPage: CLIENT_BILLING_ACTIVITY_VISIBLE_ROWS,
    persistClickedItem,
  })

  const refresh = useSelector(getShouldRefreshData)
  const estimatePageData = useSelector(getEstimatePageData)
  const estimateTotalCount = useSelector(getEstimateTotalCount)
  const isFetchingEstimate = useSelector(getEstimateLoading)
  const isSavingInvoice = useSelector(getFinanceIsSaving)
  const isPolling = useSelector(getIsPollingEstimates)
  const error = useSelector(getClientBillingActivityEstimateError)

  const isLoading = isPolling ? false : isFetchingEstimate || isSavingInvoice

  useEffect(() => {
    if (clientId && refresh) {
      dispatch(
        fetchEstimatePage({
          clientId,
          offset: offsetValue,
          limit: CLIENT_BILLING_ACTIVITY_VISIBLE_ROWS,
          isRefreshing: true,
        }),
      )
    }
  }, [clientId, refresh])

  useInterval(() => {
    if (clientId) {
      dispatch(
        fetchEstimatePage({
          clientId,
          offset: offsetValue,
          limit: CLIENT_BILLING_ACTIVITY_VISIBLE_ROWS,
          isPolling: true,
        }),
      )
    }
  }, Defaults.BALANCE_PAGE_UPDATE_INTERVAL)

  useEffect(() => {
    if (clientId) {
      dispatch(
        fetchEstimatePage({
          clientId,
          offset: offsetValue,
          limit: CLIENT_BILLING_ACTIVITY_VISIBLE_ROWS,
        }),
      )
    }
  }, [clientId])

  const handleFetchMore = (offset: number) => {
    if (clientId) {
      setURLState(offset)
      dispatch(
        fetchEstimatePage({
          clientId,
          offset,
          limit: CLIENT_BILLING_ACTIVITY_VISIBLE_ROWS,
        }),
      )
    }
  }

  if (error) {
    return (
      <Text px={2} py={1} variant="body2">
        {error}
      </Text>
    )
  }

  if (!isLoading && R.isEmpty(estimatePageData)) {
    return (
      <Text px={2} py={1} variant="body2">
        {t('Common:NO_ESTIMATES')}
      </Text>
    )
  }

  return (
    <>
      <EstimateBillingActivityTable
        estimates={estimatePageData}
        estimatesPerPage={CLIENT_BILLING_ACTIVITY_VISIBLE_ROWS}
        isLoading={isLoading}
        onEstimateNameClick={onClickEstimate}
      />
      {!isExpanded && (
        <Box display="flex" justifyContent="flex-end" p={1}>
          <PuiPagination
            initialPage={initialPage}
            itemsPerPage={CLIENT_BILLING_ACTIVITY_VISIBLE_ROWS}
            totalItems={estimateTotalCount}
            onFetchMore={handleFetchMore}
          />
        </Box>
      )}
    </>
  )
}
